import { lazy, Suspense, useEffect, useRef, useState } from "react";

// Lazy-load the quote form (react-hook-form + zod) so it stays out of the
// initial bundle. It loads when its container nears the viewport (400px
// rootMargin) or immediately if IntersectionObserver is unavailable.
const QuoteForm = lazy(() =>
  import("@/components/site/QuoteForm").then((m) => ({ default: m.QuoteForm })),
);

/**
 * Same-size placeholder so loading the form never pushes content down.
 * Matches the form card's rounded border + min height.
 */
function Placeholder() {
  return (
    <div
      aria-hidden
      className="animate-pulse rounded-3xl border-2 border-primary/25 bg-card p-6 shadow-[0_30px_70px_-45px_rgba(224,33,138,0.4)] md:p-8"
      style={{ minHeight: 640 }}
    />
  );
}

export function LazyQuoteForm({ page = "Contact" }: { page?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const el = ref.current;
    if (!el) return;
    if (
      typeof IntersectionObserver === "undefined" ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      setShow(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShow(true);
            io.disconnect();
          }
        }
      },
      { rootMargin: "400px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className="min-h-[640px]">
      {show ? (
        <Suspense fallback={<Placeholder />}>
          <QuoteForm page={page} />
        </Suspense>
      ) : (
        <Placeholder />
      )}
    </div>
  );
}
